import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PLANS, CURRENCY } from '@/lib/pricing';
import { apiService } from '@/lib/services/api';
import { useSubscription } from '@/lib/hooks/useSubscription';
import { CurrentPlanBanner } from '@/components/CurrentPlanBanner';
import { UpgradeConfirmationModal } from '@/components/UpgradeConfirmationModal';

interface StripePrice {
  id: string;
  productId: string;
  unitAmount: number;
  currency: string;
  interval: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
}

export default function SubscriptionPage() {
  const { subscription, team } = useSubscription();
  const hasActiveSubscription = !!team?.stripeSubscriptionId;
  const isCurrentlyTrialing = subscription?.isTrialing ?? false;
  const currentPlanName = subscription?.planName ?? null;

  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    priceId: string;
    planName: string;
  } | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [pricesRes, productsRes] = await Promise.all([
          apiService.get<StripePrice[]>('/api/stripe/prices'),
          apiService.get<StripeProduct[]>('/api/stripe/products'),
        ]);
        setPrices(pricesRes);
        setProducts(productsRes);
      } catch (error) {
        console.error('Failed to fetch Stripe data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Map Stripe products to our plan keys
  const starterProduct = products.find((product) => product.name === 'Starter');
  const proProduct = products.find((product) => product.name === 'Pro');

  const starterStripePrice = prices.find((price) => price.productId === starterProduct?.id);
  const proStripePrice = prices.find((price) => price.productId === proProduct?.id);

  const handlePlanSelect = (priceId: string | undefined, planName: string) => {
    if (!priceId) return;

    // If user has an active subscription, show confirmation modal
    if (hasActiveSubscription) {
      setPendingUpgrade({ priceId, planName });
      setShowUpgradeModal(true);
      return;
    }

    // New user - proceed to checkout
    handleCheckout(priceId);
  };

  const handleCheckout = async (priceId: string) => {
    setUpgradeLoading(true);
    try {
      const response = await apiService.post<{ url: string }>('/api/stripe/checkout', {
        priceId,
      });
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!pendingUpgrade) return;

    setUpgradeLoading(true);

    try {
      const response = await apiService.post('/api/stripe/change-plan', {
        priceId: pendingUpgrade.priceId,
        planName: pendingUpgrade.planName,
      });

      if (response) {
        window.location.href = '/dashboard/campaigns?upgrade=success';
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      setShowUpgradeModal(false);
    } finally {
      setUpgradeLoading(false);
      setPendingUpgrade(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-background">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-background" style={{ contain: 'layout paint' }}>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-medium text-content-heading mb-2">
            {hasActiveSubscription ? 'Manage your plan' : 'Choose your plan'}
          </h2>
          <p className="text-content-secondary">
            {hasActiveSubscription
              ? 'View your current plan or upgrade to unlock more features'
              : 'Choose the plan that fits your needs'}
          </p>
        </div>

        {isCurrentlyTrialing && subscription && (
          <div className="max-w-2xl mx-auto mb-8">
            <CurrentPlanBanner
              planName={subscription.planName ?? 'trial'}
              trialDaysRemaining={subscription.trialDaysRemaining ?? 0}
              trialEndsAt={subscription.trialEndsAt ?? new Date()}
            />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
          <PricingCard
            name={PLANS.starter.name}
            price={PLANS.starter.pricing.monthly.price}
            interval="month"
            features={[...PLANS.starter.features]}
            priceId={starterStripePrice?.id}
            isCurrentPlan={currentPlanName === 'Starter'}
            isTrialing={isCurrentlyTrialing && currentPlanName === 'Starter'}
            onSelect={() => handlePlanSelect(starterStripePrice?.id, 'Starter')}
            loading={upgradeLoading}
          />
          <PricingCard
            name={PLANS.pro.name}
            price={PLANS.pro.pricing.monthly.price}
            interval="month"
            features={[...PLANS.pro.features]}
            priceId={proStripePrice?.id}
            isCurrentPlan={currentPlanName === 'Pro'}
            isTrialing={isCurrentlyTrialing && currentPlanName === 'Pro'}
            onSelect={() => handlePlanSelect(proStripePrice?.id, 'Pro')}
            loading={upgradeLoading}
          />
        </div>

        {hasActiveSubscription && isCurrentlyTrialing && (
          <p className="text-center text-sm text-muted-foreground mt-8">
            Upgrading or changing plans will end your trial immediately and billing will start today.
          </p>
        )}
      </main>

      <UpgradeConfirmationModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setPendingUpgrade(null);
        }}
        onConfirm={handleConfirmUpgrade}
        currentPlan={currentPlanName ?? ''}
        targetPlan={pendingUpgrade?.planName ?? ''}
        trialDaysRemaining={subscription?.trialDaysRemaining ?? 0}
        loading={upgradeLoading}
      />
    </section>
  );
}

function PricingCard({
  name,
  price,
  interval,
  features,
  priceId,
  isCurrentPlan,
  isTrialing,
  onSelect,
  loading,
}: {
  name: string;
  price: number;
  interval: string;
  features: string[];
  priceId?: string;
  isCurrentPlan?: boolean;
  isTrialing?: boolean;
  onSelect: () => void;
  loading?: boolean;
}) {
  const getButtonText = () => {
    if (isCurrentPlan && isTrialing) {
      return 'Current Plan (Trial)';
    }
    if (isCurrentPlan) {
      return 'Current Plan';
    }
    return `Upgrade to ${name}`;
  };

  return (
    <Card className="px-6 py-6">
      <div className="flex flex-col justify-between h-full">
        <div>
          <div className="flex items-center gap-2 mb-6">
            <h2 className="text-2xl font-medium text-content-heading">{name}</h2>
            {isCurrentPlan && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                Current
              </span>
            )}
          </div>
          <p className="text-4xl font-medium text-content-heading mb-6">
            {CURRENCY.symbol}{price / 100}{' '}
            <span className="text-xl font-normal text-content-secondary">
              /{interval}
            </span>
          </p>
          <ul className="space-y-3 mb-8">
            {features.map((feature, index) => (
              <li key={index}>
                <span className="text-content-body text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <Button
          type="button"
          disabled={loading || !priceId || isCurrentPlan}
          variant={isCurrentPlan ? 'secondary' : 'primary'}
          className="w-full rounded-full"
          onClick={onSelect}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin mr-2 h-4 w-4" />
              Loading...
            </>
          ) : (
            getButtonText()
          )}
        </Button>
      </div>
    </Card>
  );
}
